// Debounce function to prevent excessive API calls
function debounce(fn, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      fn(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 *  @class
 *  @function Predictive Search
 */
if (!customElements.get("search-form")) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();
      this.highlightTerms = this.highlightTerms.bind(this); // Bind the method
    }
    connectedCallback() {
      this.form = this.querySelector("form[id^='header-search-form']");
      this.input = this.querySelector("input[id^='header-search-input']");
      this.predictiveSearchResults = this.querySelector(
        ".thb-predictive-search"
      );
      this.cc = this.querySelector(".searchform--click-capture");

      if (this.form && this.input && this.predictiveSearchResults && this.cc) {
        this.setupEventListeners();
      }
    }
    setupEventListeners() {
      this.form.addEventListener("submit", this.onFormSubmit.bind(this));
      this.predictiveSearchResults.addEventListener(
        "focusout",
        this.close.bind(this)
      );

      this.input.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );

      this.cc.addEventListener("click", this.close.bind(this));
      document.addEventListener("click", (event) => {
        // If the clicked element is not inside the current search form, close the predictive search.
        if (!this.contains(event.target)) {
          this.close();
        }
      });
      document.addEventListener("keyup", (e) => {
        if (e.code) {
          if (e.code.toUpperCase() === "ESCAPE") {
            this.close();
          }
        }
      });
      this.input.addEventListener("focus", this.open.bind(this));
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        document.body.classList.remove("open-search");
        this.predictiveSearchResults.setAttribute("inert", "");
        return;
      }
      document.body.classList.add("open-search");
      this.predictiveSearchResults.removeAttribute("inert");
      this.getSearchResults(searchTerm);
    }

    onFormSubmit(event) {
      if (!this.getQuery().length) {
        event.preventDefault();
      }
    }

    onFocus() {
      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        return;
      }

      this.getSearchResults(searchTerm);
    }

    getSearchResults(searchTerm) {
      this.predictiveSearchResults.classList.add("loading");

      fetch(
        `${theme.routes.predictive_search_url}?q=${encodeURIComponent(
          searchTerm
        )}&${encodeURIComponent(
          "resources[type]"
        )}=page,product,article,query,collection&${encodeURIComponent(
          "resources[limit]"
        )}=10&section_id=predictive-search&${encodeURIComponent(
          "resources[options][fields]"
        )}=title,body`
      )
        .then((response) => {
          this.predictiveSearchResults.classList.remove("loading");
          if (!response.ok) {
            var error = new Error(response.status);
            throw error;
          }

          return response.text();
        })
        .then((text) => {
          const resultsMarkup = new DOMParser()
            .parseFromString(text, "text/html")
            .querySelector("#shopify-section-predictive-search").innerHTML;

          this.renderSearchResults(resultsMarkup);
          this.highlightTerms(); // Call the highlightTerms function after rendering
        })
        .catch((error) => {
          throw error;
        });
    }

    renderSearchResults(resultsMarkup) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;

      let submitButton = this.querySelector("#search-results-submit");

      submitButton.addEventListener("click", () => {
        this.form.submit();
      });
    }

    close() {
      this.predictiveSearchResults.setAttribute("inert", "");
      document.body.classList.remove("open-search");
    }
    open() {
      document.body.classList.add("open-search");
      this.predictiveSearchResults.removeAttribute("inert");
    }

    highlightTerms() {
      const searchResults = this.predictiveSearchResults.querySelectorAll(
        ".thb-predictive-search--content"
      );
      const searchTerm = this.getQuery();
      const searchTerms = searchTerm.split(" ").filter(term => term);

      searchResults.forEach((result) => {
        const titleElement = result.querySelector(".cart-product-link");
        const originalText = titleElement.textContent;

        const patterns = searchTerms.map(term => {
          const normalizedTerm = term.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          const escapedTerm = normalizedTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          
          return escapedTerm
            .split('')
            .map(char => {
              const diacriticMap = {
                'a': '[aàáâãäåāăąǎǻ]',
                'e': '[eèéêëēĕėęě]',
                'i': '[iìíîïĩīĭįı]',
                'o': '[oòóôõöøōŏő]',
                'u': '[uùúûüũūŭůűų]',
                'c': '[cçćĉċč]',
                'n': '[nñńņň]',
                'y': '[yýÿŷ]'
              };
              return diacriticMap[char.toLowerCase()] || char;
            })
            .join('');
        });

        const combinedPattern = patterns
          .sort((a, b) => b.length - a.length)
          .join('|');
        
        const regex = new RegExp(`(${combinedPattern})`, 'gi');
        const highlightedText = originalText.replace(regex, '<span class="highlight">$1</span>');

        titleElement.innerHTML = highlightedText;
      });
    }
  }
  customElements.define("search-form", SearchForm);
}
